import React from 'react';
import classNames from 'classnames';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import PropTypes from 'prop-types';
import { Tooltip } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Button1 from 'Components/ComponentLibrary/Components/Buttons/Button1';

const VISUAL_WIDTH = 2;

const VISUAL_COLUMN_COMMON_STYLES = {
  position: 'absolute',
  pointerEvents: 'none',
  top: 0,
  left: '50%',
  bottom: 0,
  width: VISUAL_WIDTH,
};

const useStyles = buildUseStyles('DraggableColumn', ((theme) => ({
  root: {
    position: 'absolute',
    width: theme.spacing(2),
    zIndex: theme.zIndex.drawer,
    top: 0,
    bottom: 0,
  },
  rootHoverable: {
    cursor: 'col-resize',
  },
  rootLeft: {
    left: 0,
    transform: 'translateX(-50%)',
  },
  rootRight: {
    right: 0,
    transform: 'translateX(50%)',
  },
  visualColumn: {
    ...VISUAL_COLUMN_COMMON_STYLES,
    width: 1,
    zIndex: theme.zIndex.drawer,
    backgroundColor: theme.palette.facet.lightGray,
  },
  visualColumnLeft: {},
  visualColumnRight: {},
  visualColumnHighlight: {
    ...VISUAL_COLUMN_COMMON_STYLES,
    width: 2,
    zIndex: theme.zIndex.drawer,
    opacity: 0,
    backgroundColor: theme.palette.primary.main,
    transition: theme.transitions.create(['opacity', 'background-color']),
  },
  visualColumnHighlightHovered: {
    opacity: 0.5,
  },
  visualColumnHighlightPressed: {
    opacity: 0.75,
    backgroundColor: theme.palette.primary.dark,
  },
  iconButton: {
    opacity: 0,
    pointerEvents: 'none',
    top: theme.dimensions.xs,
    borderRadius: '9999px',
    minWidth: 0,
    left: '50%',
    padding: theme.spacing(0.5),
    transform: 'translateX(-50%)',
    zIndex: theme.zIndex.drawer,
    transition: theme.transitions.create(['opacity', 'background-color']),
  },
  iconButtonHovered: {
    opacity: 1,
    pointerEvents: 'auto',
  },
})));

const DraggableColumn = ({
  onDragStart,
  onToggleCollapsed,
  isCollapsed,
  side,
  alwaysShowDivider,
}) => {
  const classes = useStyles();
  const [isHovering, setIsHovering] = React.useState(false);
  const [hasMouseDown, setHasMouseDown] = React.useState(false);
  const isOnLeft = side === 'left';
  const isOnRight = !isOnLeft;

  const onMouseDown = (evt) => {
    setHasMouseDown(true);
    onDragStart(evt);
  };

  const onMouseUp = () => {
    setHasMouseDown(false);
  };

  useComponentDidMount(() => {
    document.addEventListener('mouseup', onMouseUp, { capture: true });
  }, () => {
    document.removeEventListener('mouseup', onMouseUp, { capture: true });
  });

  const collapsedIcon = isOnLeft ? <ChevronLeftIcon /> : <ChevronRightIcon />;
  const expandIcon = isOnLeft ? <ChevronRightIcon /> : <ChevronLeftIcon />;
  const isDraggable = !isCollapsed;

  const doIfDraggable = (fn) => (evt) => {
    if (isDraggable) {
      fn(evt);
    }
  };

  return (
    <Box
      className={classNames(classes.root, {
        [classes.rootLeft]: isOnLeft,
        [classes.rootRight]: isOnRight,
        [classes.rootHoverable]: isDraggable && (isHovering || alwaysShowDivider),
      })}
      onMouseDown={doIfDraggable(onMouseDown)}
      onMouseEnter={doIfDraggable(() => {
        setIsHovering(true);
      })}
      onMouseLeave={doIfDraggable(() => {
        setIsHovering(false);
      })}
    >
      <Box className={classNames(classes.visualColumn, {
        [classes.visualColumnLeft]: isOnLeft,
        [classes.visualColumnRight]: isOnRight,
      })}
      />
      <Box
        className={classNames(classes.visualColumnHighlight, {
          [classes.visualColumnLeft]: isOnLeft,
          [classes.visualColumnRight]: isOnRight,
          [classes.visualColumnHighlightHovered]: (isHovering || alwaysShowDivider),
          [classes.visualColumnHighlightPressed]: hasMouseDown,
        })}
      />
      {onToggleCollapsed ? (
        <Tooltip title={isCollapsed ? 'Expand' : 'Collapse'}>
          <Button1
            test="draggable-column-toggle-expansion-button"
            className={classNames(classes.iconButton, {
              [classes.iconButtonHovered]: isHovering || alwaysShowDivider || isCollapsed,
            })}
            color={'accentBlue'}
            variant={'contained'}
            disableElevation={false}
            onMouseDown={(evt) => {
              evt.stopPropagation();
              evt.preventDefault();
            }}
            onMouseUp={(evt) => {
              evt.stopPropagation();
              evt.preventDefault();
            }}
            onClick={(evt) => {
              evt.stopPropagation();
              evt.preventDefault();
              onToggleCollapsed();
            }}
          >
            {isCollapsed ? collapsedIcon : expandIcon}
          </Button1>
        </Tooltip>
      ) : null}
    </Box>
  );
};

DraggableColumn.defaultProps = {
  onToggleCollapsed: null,
  isCollapsed: null,
};

DraggableColumn.propTypes = {
  side: PropTypes.oneOf(['left', 'right']).isRequired,
  onDragStart: PropTypes.func.isRequired,
  onToggleCollapsed: PropTypes.func,
  isCollapsed: PropTypes.bool,
};

export default DraggableColumn;
